import React from "react";

import { Grid } from "@material-ui/core";
import Big from "big.js";
import { useField } from "formik";
import { useSelector } from "react-redux";

import { SubtleBadge } from "components/Badge";
import { CollapseLabel, FormCollapse } from "components/Form";
import { withNamespace } from "components/Form/FormikControls";
import { VendorCommissionBands } from "components/Form/FormikControls/VendorCommissionBands";
import { Row } from "components/Layout";

import { CommissionTypes } from "constants/commissions";

import { getSaleSubTypeById, getSpeciesById } from "selectors";

interface VendorCommissionEntryProps {
  entry: VendorCommissionBand;
}

function VendorCommissionEntry({ entry }: VendorCommissionEntryProps) {
  const {
    saleSubTypeId,
    saleType,
    speciesId,
    commissionType,
    commissionValue = 0,
  } = entry;
  const saleSubType = useSelector(getSaleSubTypeById(saleSubTypeId));
  const species = useSelector(getSpeciesById(speciesId));

  const amountText =
    commissionType === CommissionTypes.DollarsPerHead
      ? `$${Big(commissionValue).div(100)}/Head`
      : `${Big(commissionValue).times(100)}%`;

  if (saleSubTypeId) {
    return `${species?.name} - ${saleType} - ${saleSubType?.name} @ ${amountText}. `;
  } else if (speciesId) {
    return `${species?.name} - ${saleType} @ ${amountText}. `;
  } else if (saleType) {
    return `${saleType} @ ${amountText}. `;
  } else {
    return `Default @ ${amountText}. `;
  }
}

interface VendorCommissionsHeaderProps {
  namespace?: string;
}

function VendorCommissionsHeader({
  namespace: ns = "",
}: VendorCommissionsHeaderProps) {
  const [{ value }] = useField(withNamespace(ns, "vendorCommissionBands"));
  const numberOfBands = value?.length || 0;

  const body =
    numberOfBands > 2
      ? `${numberOfBands} Rates`
      : value?.map((entry: VendorCommissionBand) => {
          return <VendorCommissionEntry entry={entry} key={entry.id} />;
        });

  return (
    <Row>
      <div>
        <SubtleBadge>Vendor Commissions</SubtleBadge>
      </div>
      <CollapseLabel>{body}</CollapseLabel>
    </Row>
  );
}

interface VendorCommissionsProps {
  isOpen: boolean;
  namespace?: string;
  onToggle: () => void;
  businessId?: string;
}

function VendorCommissions({
  isOpen,
  namespace: ns = "",
  onToggle,
}: VendorCommissionsProps) {
  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={<VendorCommissionsHeader namespace={ns} />}
      dataTour="vendorCommissionsCollapse"
    >
      <Grid item xs={12}>
        <VendorCommissionBands
          name="vendorCommissionBands"
          namespace={ns}
          tooltip="Enter commission rates specific to this vendor.  These will override the defaults set for your agency."
        />
      </Grid>
    </FormCollapse>
  );
}

export default VendorCommissions;
